import React, { useEffect, useState } from "react";
import {
  Box, Button, Paper, Typography, Alert,
  TextField,
  IconButton,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const API_BASE_URL = 'https://api.neontradepro.com';

interface UserReferralsItem {
  first_name: string;
  last_name: string;
  referral_commission: number;
}

function validateReferralCode(input: string): boolean {
  const minLength = 5;
  const maxLength = 32;
  const pattern = /^[a-zA-Z0-9]+$/; 

  if (input.length < minLength || input.length > maxLength) {
    return false;
  }

  if (!pattern.test(input)) {
    return false;
  }

  return true;
}

function AccountManagement() {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'warning' | 'error' | 'info'>('info');
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [isSettingReferralCode, setIsSettingReferralCode] = useState<boolean>(false);
  const [newReferralCodeInput, setNewReferralCodeInput] = useState<string>("");
  const [isSubmittingReferralCode, setIsSubmittingReferralCode] = useState<boolean>(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState<boolean>(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState<string>("");
  const [referralCodeInputError, setReferralCodeInputError] = useState<string>(""); 
  const [loading, setLoading] = useState<boolean>(true);
  const [userReferrals, setUserReferrals] = useState<UserReferralsItem[]>([]);

  useEffect(()=>{
    const fetchReferralCode = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setAlertMessage('توکن احراز هویت یافت نشد. لطفاً وارد شوید.');
        setAlertSeverity('error');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/user/info`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'خطا در دریافت اطلاعات کاربر');
        }

        const userinfo = await response.json();
        setReferralCode(userinfo.referral_code);
        fetchReferralList();
      } catch (err: any) {
        setAlertMessage(err.message);
        setAlertSeverity('error');
      } finally {
        setLoading(false);
      }
    }
    const fetchReferralList = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setShowAlert(true);
        setAlertMessage('توکن احراز هویت یافت نشد. لطفاً وارد شوید.');
        setAlertSeverity('error');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/user/referrals`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'خطا در دریافت کاربران دعوت شده');
        }

        const referralList = await response.json();
        setUserReferrals(referralList);
      } catch (err: any) {
        setShowAlert(true);
        setAlertMessage(err.message);
        setAlertSeverity('error');
      } finally {
        setLoading(false);
      }
    }
    fetchReferralCode();
  }, []);

  const displayAlert = (message: string, severity: 'success' | 'warning' | 'error' | 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  const handleShowSetReferralCode = () => {
    setIsSettingReferralCode(true);
    setNewReferralCodeInput(""); 
    setReferralCodeInputError(""); 
  };

  const handleCancelSetReferralCode = () => {
    setIsSettingReferralCode(false);
    setNewReferralCodeInput("");
    setReferralCodeInputError(""); 
  };

  const handleNewReferralCodeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewReferralCodeInput(value);
    if (value.length > 0 && !validateReferralCode(value)) {
      setReferralCodeInputError('کد معرف باید شامل حروف انگلیسی و اعداد باشد و طول آن بین 5 تا 32 کاراکتر باشد.');
    } else {
      setReferralCodeInputError('');
    }
  };

  const handleConfirmAndSaveReferralCode = async () => {
    if (newReferralCodeInput.trim() === "") {
      displayAlert("کد معرف نمی‌تواند خالی باشد.", "error");
      setReferralCodeInputError('کد معرف نمی‌تواند خالی باشد.'); 
      return;
    }

    if (!validateReferralCode(newReferralCodeInput)) {
      displayAlert("فرمت کد معرف نامعتبر است. لطفاً راهنما را مطالعه کنید.", "error");
      setReferralCodeInputError('کد معرف باید شامل حروف انگلیسی و اعداد باشد و طول آن بین 5 تا 32 کاراکتر باشد.');
      return;
    }

    setIsSubmittingReferralCode(true);
    setAlertMessage("");

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("توکن احراز هویت یافت نشد. لطفاً مجدداً وارد شوید.");
      }

      const response = await fetch(`${API_BASE_URL}/user/settings/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ referral_code: newReferralCodeInput }),
      });

      if (!response.ok) {
        let errorMsg = `خطا در ثبت کد معرف. کد وضعیت: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData && typeof errorData === 'object') {
            const messages = Object.values(errorData).flat();
            if (messages.length > 0) {
              errorMsg = messages.join(' ');
            }
          } else if (typeof errorData === 'string' && errorData.length > 0) {
            errorMsg = errorData;
          }
        } catch (e) {
          console.error("Could not parse error JSON or empty response", e);
        }
        throw new Error(errorMsg);
      }

      setReferralCode(newReferralCodeInput);
      setIsSettingReferralCode(false);
      displayAlert("کد معرف شما با موفقیت ثبت شد.", "success");

    } catch (error: any) {
      console.error("Error saving referral code:", error);
      displayAlert(error.message || "خطای ناشناخته در هنگام ثبت کد معرف.", "error");
    } finally {
      setIsSubmittingReferralCode(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode)
        .then(() => {
          setCopySnackbarMessage("کد معرف کپی شد!");
          setCopySnackbarOpen(true);
        })
        .catch(err => {
          console.error("خطا در کپی کد معرف: ", err);
          setCopySnackbarMessage("خطا در کپی کد!");
          setCopySnackbarOpen(true);
        });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {showAlert && (
         <Alert severity={alertSeverity} sx={{ mb: 2, width: '100%', '.MuiAlert-message': {width: '100%'} }}>
          {alertMessage}
        </Alert>
      )}

      <Paper className="themed-paper-card">
        <Typography variant="h6" sx={{ mb: 2 }}>کد معرف شما</Typography>
        {referralCode === null && !isSettingReferralCode && (
          <>
            <Typography variant="body2" sx={{ color: "var(--link-color)", mb: 2 }}>
              هنوز کد معرف برای خود تنظیم نکرده‌اید. با تنظیم کد معرف می‌توانید دوستان خود را دعوت کنید.
              این کد پس از ثبت، قابل تغییر نخواهد بود.
            </Typography>
             <Button
                onClick={handleShowSetReferralCode}
                disabled={isSubmittingReferralCode}
                sx={{
                    fontFamily: 'Vazirmatn, Arial, sans-serif',
                    backgroundColor: '#15A3EF',
                    color: '#333',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    textTransform: 'none',

                    '&.Mui-disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#a0a0a0',
                    },

                }}
            >
                افزودن کد معرف
            </Button>
          </>
        )}
        {isSettingReferralCode && (
          <Box>
            <Typography variant="body2" sx={{ color: "var(--link-color)", mb: 2,fontFamily: 'Vazirmatn, sans-serif'}}>
              یک کد معرف دلخواه برای خود وارد کنید (این کد پس از ثبت قابل تغییر نیست).
              <br/>
              **قوانین:** فقط حروف انگلیسی (a-z, A-Z) و اعداد (0-9). طول 5 تا 32 کاراکتر.
            </Typography>
            <TextField
              fullWidth
              label="کد معرف دلخواه"
              variant="outlined"
              className="themed-text-field"
              value={newReferralCodeInput}
              onChange={handleNewReferralCodeInputChange} 
              sx={{ mb: 2, direction: 'ltr' }}
              disabled={isSubmittingReferralCode}
              error={!!referralCodeInputError} 
              helperText={referralCodeInputError} 
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                className='panel-btn'
                onClick={handleConfirmAndSaveReferralCode}
                disabled={isSubmittingReferralCode || !!referralCodeInputError || newReferralCodeInput.trim() === ""} 
              >
                {isSubmittingReferralCode ? <CircularProgress size={24} color="inherit" /> : "تایید و ثبت کد"}
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCancelSetReferralCode} disabled={isSubmittingReferralCode}>
                انصراف
              </Button>
            </Box>
          </Box>
        )}
        {referralCode !== null && !isSettingReferralCode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color: 'var(--text-color)',
                border: '1px solid var(--border-color)',
                padding: '0.5rem 1rem',
                fontFamily: 'Vazirmatn, sans-serif',
                borderRadius: '8px',
                direction: 'ltr',
                flexGrow: 1,
                textAlign: 'left',
                userSelect: 'all',
              }}
            >
              {referralCode}
            </Typography>
            <IconButton onClick={handleCopyToClipboard} title="کپی کردن کد">
              <ContentCopyIcon sx={{ color: 'var(--primary-color)' }} />
            </IconButton>
          </Box>
        )}
      </Paper>
       <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
            تعداد افرادی که دعوت کردید: {userReferrals.length} نفر
          </Typography>
      {userReferrals.length > 0 && (
        <Paper className="themed-paper-card">
          <Typography variant="h6" sx={{ mb: 2 }}>کاربران دعوت شده</Typography>

          <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                      <tr>
                          <th scope="col" className="px-6 py-3">
                              ردیف
                          </th>
                          <th scope="col" className="px-6 py-3">
                              نام
                          </th>
                          <th scope="col" className="px-6 py-3">
                              کمیسیون
                          </th>
                      </tr>
                  </thead>
                  <tbody>
                    {userReferrals.map((user, index)=>(
                      <tr key={index} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200">
                          <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                              {index+1}
                          </th>
                          <td className="px-6 py-4">
                              {user.first_name} {user.last_name}
                          </td>
                          <td className="px-6 py-4">
                              ${user.referral_commission/100}
                          </td>
                      </tr>
                    ))}
                  </tbody>
              </table>
          </div>
        </Paper>
      )}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message={copySnackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default AccountManagement;